import type { TaskAnimalV1, TaskAssignmentReason } from '@/model/Task.model';

const capitalize = (str: string): string => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

const abbreviateDay = (day: string): string => {
  const abbreviations: Record<string, string> = {
    MONDAY: 'Mon',
    TUESDAY: 'Tue',
    WEDNESDAY: 'Wed',
    THURSDAY: 'Thu',
    FRIDAY: 'Fri',
    SATURDAY: 'Sat',
    SUNDAY: 'Sun',
  };
  return abbreviations[day] || day;
};

/**
 * Formats a sorted array of age values into a summary string with truncation
 * for lists longer than 3 values (shows first 2 then count of remaining).
 */
const formatAgeValues = (sorted: Array<number>): string => {
  const n = sorted.length;
  if (n === 1) {
    return `Age: ${sorted[0]} ${sorted[0] === 1 ? 'Day' : 'Days'}`;
  }
  if (n === 2) {
    return `Age: ${sorted[0]} & ${sorted[1]} Days`;
  }
  if (n === 3) {
    return `Age: ${sorted[0]}, ${sorted[1]} & ${sorted[2]} Days`;
  }
  const remaining = n - 2;
  return `Age: ${sorted[0]}, ${sorted[1]} & ${remaining} more`;
};

/**
 * Formats a sorted array of day values with a label prefix, using truncation
 * for lists longer than 3 values (shows first 2 then count of remaining).
 */
const formatDayValues = (label: string, sorted: Array<number>): string => {
  const n = sorted.length;
  if (n === 1) {
    return `${label}: Day ${sorted[0]}`;
  }
  if (n === 2) {
    return `${label}: Days ${sorted[0]} & ${sorted[1]}`;
  }
  if (n === 3) {
    return `${label}: Days ${sorted[0]}, ${sorted[1]} & ${sorted[2]}`;
  }
  const remaining = n - 2;
  return `${label}: Days ${sorted[0]}, ${sorted[1]} & ${remaining} more`;
};

/**
 * Formats a task assignment reason into a human-readable string.
 */
export const formatAssignmentReason = (reason: TaskAssignmentReason): string => {
  switch (reason.type) {
    case 'age':
      return `Age: ${reason.value} ${reason.value === 1 ? 'Day' : 'Days'}`;
    case 'study_day':
      return `Study: Day ${reason.value}`;
    case 'tracking_day':
      return `Tracking: Day ${reason.value}`;
    case 'recurring_daily':
      if (reason.occurrence != null && reason.total != null) {
        return `Recurring: Day ${reason.occurrence} of ${reason.total}`;
      }
      return 'Recurring daily';
    case 'recurring_weekly':
      if (reason.occurrence != null && reason.total != null) {
        return `Recurring: Week ${reason.occurrence} of ${reason.total} (${abbreviateDay(reason.day_of_week)})`;
      }
      return capitalize(reason.day_of_week);
    case 'one_off':
      return '';
    case 'unknown':
      return '';
  }
};

/**
 * Summarizes task assignment reasons across all animals into a concise single-line overview.
 * Ignores 'unknown' and 'one_off' types. Returns empty string if no valid reasons exist.
 */
export const summarizeAssignmentReasons = (animals: Array<TaskAnimalV1>): string => {
  const ageValues = new Set<number>();
  const studyDayValues = new Set<number>();
  const trackingDayValues = new Set<number>();
  let recurringDailyReason: TaskAssignmentReason | null = null;
  let recurringWeeklyReason: TaskAssignmentReason | null = null;

  for (const animal of animals) {
    const reason = animal.assignment_reason;
    switch (reason.type) {
      case 'age':
        ageValues.add(reason.value);
        break;
      case 'study_day':
        studyDayValues.add(reason.value);
        break;
      case 'tracking_day':
        trackingDayValues.add(reason.value);
        break;
      case 'recurring_daily':
        recurringDailyReason = reason;
        break;
      case 'recurring_weekly':
        recurringWeeklyReason = reason;
        break;
      case 'one_off':
      case 'unknown':
        break;
    }
  }

  if (ageValues.size > 0) {
    const sorted = Array.from(ageValues).sort((a, b) => a - b);
    return formatAgeValues(sorted);
  }

  if (studyDayValues.size > 0) {
    const sorted = Array.from(studyDayValues).sort((a, b) => a - b);
    return formatDayValues('Study', sorted);
  }

  if (trackingDayValues.size > 0) {
    const sorted = Array.from(trackingDayValues).sort((a, b) => a - b);
    return formatDayValues('Tracking', sorted);
  }

  if (recurringDailyReason !== null) {
    return 'Recurring';
  }

  if (recurringWeeklyReason !== null) {
    return 'Recurring';
  }

  return '';
};
